import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Health, type SipHealth } from './api';
import { useSoftphone } from '@/store/softphone';
import { useBackendOnline } from '@/store/connection';

/**
 * Subscribe to the cached SIP health query. Use this in any component that
 * wants to *read* the live 3CX-side registration status. Cheap — React
 * Query dedupes by queryKey so calling this from multiple components issues
 * a single network request every 30s.
 *
 * Does NOT trigger any recovery side effects. Use `useSipHealthRecovery`
 * for that, and mount it exactly once at the AppShell level.
 */
export function useSipHealthQuery(): SipHealth | undefined {
  const client = useSoftphone((s) => s.client);
  const sipState = useSoftphone((s) => s.state);
  const networkOffline = useSoftphone((s) => s.networkOffline);
  const backendOnline = useBackendOnline();
  /** Wall-clock when we entered 'registered' state, 0 when we're not.
   *  Reset on any state transition away from 'registered'.
   *
   *  Set DURING render (not in useEffect) — useEffect runs AFTER render so
   *  the very first render where sipState=='registered' would leave the
   *  ref at 0 and skip the settle override, letting useSipHealthRecovery
   *  see the stale xapi data and trigger a spurious force-register loop.
   *  Mutating refs during render is allowed by React for caching. */
  const registeredSinceRef = useRef(0);
  if (sipState === 'registered') {
    if (registeredSinceRef.current === 0) registeredSinceRef.current = Date.now();
  } else if (registeredSinceRef.current !== 0) {
    registeredSinceRef.current = 0;
  }

  const { data } = useQuery({
    queryKey: ['sip-health'],
    // CRITICAL: catch HTTP errors INSIDE queryFn and translate to a
    // pbx_reachable=false SipHealth fallback. Without this, React Query
    // keeps the last successful response on error, so the UI keeps showing
    // "Hazır" even while backend → 3CX is returning 502/500 (NGINX gateway
    // timeout when 3CX is restarting). Now any failed probe immediately
    // tells useConnectionHealthy "PBX down" → timer freezes, status pill
    // shows "Kontrol ediliyor".
    queryFn: async () => {
      try {
        return await Health.sipRegistration();
      } catch (e: any) {
        console.warn('[sip-health] probe failed, treating as PBX unreachable:', e?.response?.status, e?.message);
        return {
          extension: '',
          registered: false,
          enabled: false,
          pbx_reachable: false,
          error: `${e?.response?.status ?? 'network'}: ${e?.message ?? 'unknown'}`,
          ts: Date.now(),
        };
      }
    },
    // Dynamic polling — when last result is unhealthy, probe every 5s so UI
    // catches up to a 3CX restart quickly. When healthy, drop to 30s to save
    // backend load.
    refetchInterval: (query) => {
      const last = query.state.data as SipHealth | undefined;
      if (!last) return 10_000;
      const healthy = last.pbx_reachable && last.registered;
      return healthy ? 30_000 : 5_000;
    },
    refetchOnWindowFocus: true,
    // Disable polling while browser reports offline VEYA backend WS'i down
    // (502 hammer + log spam olmasın). Backend dönünce ('live') yeniden başlar.
    enabled: !!client && !networkOffline && backendOnline,
    // No retry — we already translate errors to fallback data above, so a
    // retry just delays the UI from showing "unhealthy" state.
    retry: false,
  });

  if (!data) return undefined;

  // Sofia-SIP local 'registered' OTORİTER kabul edilir: Sofia kayıtlıyken
  // (200 OK aldı = gerçekten kayıtlıyız) ama xapi IsRegistered=false diyorsa
  // LOKAL'e güven. xapi IsRegistered GÜVENİLMEZ (lag/yanlış, özellikle
  // registration churn'de) — ona dayanıp UI'da "Yeniden bağlanıyor" göstermek +
  // forceRegister tetiklemek YIKICI LOOP yaratıyordu (mismatch → re-register →
  // REGISTER stall → register_failed → registered → mismatch → ...). Sofia zaten
  // TTL (~60s) ile kendini yeniler → gerçek staleness orada churn'süz toparlanır.
  // SÜREKLİ xapi-mismatch = 3CX TARAFI sorunu (9998 başka endpoint'ten de register,
  // NAT/contact, xapi cache gecikmesi) — client re-register ÇÖZMEZ, server'da bakılır.
  // (Eski 30s SETTLE penceresi 30s sonra mismatch'e düşüp loop'u başlatıyordu.)
  if (data.pbx_reachable && !data.registered && registeredSinceRef.current > 0) {
    return { ...data, registered: true };
  }

  return data;
}

/**
 * Mount ONCE at AppShell level. Watches the SIP health query and triggers
 * `client.forceRegister()` when local Janus state diverges from real 3CX
 * registration status.
 *
 * Recovery rules:
 *  - `registered=false` + local state=registered → mismatch, force re-register
 *  - `pbx_reachable=false` 3+ times in a row → backend can't see 3CX, assume
 *    stale and force re-register anyway. Without this we'd just wait forever
 *    in "Hazır" while the PBX is silently down.
 *
 * The hook does NOT call forceRegister in a tight loop — JanusSip itself
 * enforces a 5s cooldown so repeated triggers during a sustained outage
 * collapse into a single REGISTER attempt every 5s.
 */
export function useSipHealthRecovery(): void {
  const health = useSipHealthQuery();
  const client = useSoftphone((s) => s.client);
  const state = useSoftphone((s) => s.state);
  const unreachableCount = useRef(0);
  /** Tracks whether the last cycle saw PBX as unreachable. On the rising
   *  edge (unreachable → reachable) we know the outage just ended and the
   *  Janus client is probably mid-backoff; trigger an immediate reconnect
   *  to skip the remaining exponential delay (was up to 30s). */
  const wasUnhealthy = useRef(false);
  /** Wall-clock of the last triggered action (forceRegister or immediate
   *  reconnect). After we trigger anything, suppress further triggers for
   *  GRACE_MS — gives Sofia-SIP's REGISTER OK time to propagate into 3CX's
   *  xapi IsRegistered cache (observed lag up to ~20s after PBX restart).
   *  Without grace, we'd hammer forceRegister on every poll because xapi
   *  reports stale `registered: false` long after SIP layer is healthy. */
  const lastActionAt = useRef(0);
  const GRACE_MS = 30_000;

  useEffect(() => {
    if (!health || !client) return;

    // queueIn'i 3CX'in gerçek QueueStatus'uyla senkron tut. Store singleton
    // olduğu için logout→login (refresh'siz) ya da refresh sonrası queueIn
    // stale kalabiliyordu; tek doğru kaynak 3CX. Toggle sırasında (queueAction)
    // dokunma — optimistic state ile yarışmasın.
    if (health.pbx_reachable && health.queue_status && !useSoftphone.getState().queueAction) {
      const realIn = health.queue_status.toLowerCase() === 'loggedin';
      if (useSoftphone.getState().queueIn !== realIn) {
        useSoftphone.setState({ queueIn: realIn });
      }
    }

    if (!health.pbx_reachable) {
      unreachableCount.current += 1;
      wasUnhealthy.current = true;
      // Mirror to store so StatusPill/useConnectionHealthy can show offline
      // UI even when navigator.onLine wrongly reports online (VPN dropped
      // but underlying Wi-Fi is still up — classic browser blind spot).
      useSoftphone.setState({ pbxUnreachableStreak: unreachableCount.current });
      console.warn('[sip-health] PBX unreachable (count=%d):', unreachableCount.current, health.error);
      if (unreachableCount.current >= 3 && state === 'registered'
          && Date.now() - lastActionAt.current > GRACE_MS) {
        console.warn('[sip-health] PBX unreachable 3+ times — assuming registration stale, forcing re-register');
        lastActionAt.current = Date.now();
        client.forceRegister();
      }
      return;
    }

    // PBX reachable — reset the streak counter.
    unreachableCount.current = 0;
    if (useSoftphone.getState().pbxUnreachableStreak !== 0) {
      useSoftphone.setState({ pbxUnreachableStreak: 0 });
    }

    // Rising edge — outage just ended. Bypass any backoff and reconnect now.
    if (wasUnhealthy.current) {
      wasUnhealthy.current = false;
      console.log('[sip-health] PBX recovered after outage — requesting immediate reconnect (skip backoff)');
      lastActionAt.current = Date.now();
      client.triggerImmediateReconnect();
      return; // skip the mismatch check this tick; recovery will resync
    }

    // Mismatch suppression during grace window — common case is xapi is
    // simply slow to reflect the just-completed REGISTER (Sofia-SIP got
    // 200 OK but 3CX's status cache hasn't updated yet).
    if (Date.now() - lastActionAt.current < GRACE_MS) return;

    if (!health.registered && state === 'registered') {
      console.warn('[sip-health] mismatch — local=registered, 3CX=unregistered. Forcing re-register');
      lastActionAt.current = Date.now();
      client.forceRegister();
    }
  }, [health, client, state]);
}
